<template>
    <div
        :class="{
            'dk-rating': true,
            'dk-rating--read-only': readOnly,
            'dk-rating--animation': animation && !readOnly,
        }"
    >
        <div v-if="!readOnly" class="dk-rating__select u-hidden">
            <select :id="`rating-${_uid}`" v-model="selectedItem.index" name="rating">
                <option value="0">
                    0 of {{ config.ratingMaxValue }}
                </option>
                <option v-for="(n, i) in config.ratingMaxValue" :key="i" :value="n">
                    {{ n }} of {{ config.ratingMaxValue }}
                </option>
            </select>
        </div>

        <div class="dk-rating__control">
            <ul :role="readOnly ? null : 'radiogroup'" class="dk-rating__items">
                <li v-if="ratingIsZero" class="dk-rating__item dk-rating__item--zero dk-rating__item--checked"></li>
                <li
                    v-for="(n, i) in config.ratingMaxValue"
                    :key="i"
                    :class="{
                        [iconColorClass]: true,
                        'dk-rating__item': true,
                        'dk-rating__item--checked': checked(i),
                        'dk-rating__item--half': isHalf(i),
                    }"
                    :role="readOnly ? null : 'radio'"
                    :aria-label="readOnly ? null : i + 1"
                    :aria-checked="readOnly ? null : checked(i) || 'false'"
                    :tabindex="readOnly ? null : checked(i) || (ratingIsZero && i === 0) ? 0 : -1"
                    @click="onRatingItemClick(i, $event)"
                >
                    <div class="dk-rating__icon">
                        <icon :name="config.iconName" />
                    </div>
                    <div v-if="readOnly && isHalf(i)" class="dk-rating__icon dk-rating__icon--inactive">
                        <icon :name="config.iconName" />
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
export const TYPE_SEAT = { name: 'seat', maxValue: 7, ratingMaxValue: 7 };
export const TYPE_BOOT = { name: 'boot', maxValue: 650, ratingMaxValue: 5 };
export const TYPE_CONSUMPTION = { name: 'consumption', maxValue: 12, ratingMaxValue: 5 };
export const TYPE_EMISSION = { name: 'emission', maxValue: 170, ratingMaxValue: 5 };
export const TYPE_DEFAULT = { name: '', maxValue: 5, ratingMaxValue: 5 };
export const TYPES = [TYPE_SEAT, TYPE_BOOT, TYPE_CONSUMPTION, TYPE_DEFAULT, TYPE_EMISSION];

/**
 * Rating component
 *
 * @version 4.0.0
 * @example ./examples/DkRating.md
 */
export default {
    name: 'DkRating',
    components: {
        Icon: () => ({
            component: import(/* webpackChunkName: "Icon-vue" */ '@v4/common/components/atoms/Icon.vue'),
        }),
    },
    props: {
        /**
         * Rating type. Available types: `seat`, `boot`, `consumption`.
         */
        type: {
            type: String,
            default: '',
            validator: (value) => TYPES.map((type) => type.name).includes(value),
        },
        /**
         * Must be a positive integer and corresponds to max items to show.
         * Min value accepted it's `2` otherwise will take the fallback value based on `type` property.
         */
        ratingMax: {
            type: Number,
            default: 0,
        },
        /**
         * Max reachable value. Must be a positive number(`> -1`).
         * If negative will take the fallback value based on `type` property.
         */
        max: {
            type: Number,
            default: -1,
        },
        /**
         * Rating value.
         */
        value: {
            type: Number,
            default: 0,
        },
        /**
         * Read only or interactive.
         */
        readOnly: {
            type: Boolean,
            default: true,
        },
        /**
         * Has animation or not.
         */
        animation: {
            type: Boolean,
            default: true,
        },
        /**
         * Value expressed as a percentage
         */
        percentage: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            selectedItem: {
                index: 0,
                el: null,
            },
        };
    },
    computed: {
        config() {
            let type = TYPES.find((type) => type.name === this.type);

            if (!type) {
                type = TYPE_DEFAULT;
            }

            let ratingMaxValue = type.ratingMaxValue;
            let maxValue = type.maxValue;

            if (this.ratingMax > 1 && Number.isInteger(this.ratingMax)) {
                ratingMaxValue = this.ratingMax;
            }

            if (this.max > -1) {
                maxValue = this.max;
            }

            const config = {
                ratingMaxValue,
                maxValue,
                iconColorClass: 'color--primary',
                iconName: 'generic-rating',
            };

            switch (type.name) {
                case TYPE_SEAT.name:
                    config.iconName = 'generic-seat';
                    break;
                case TYPE_BOOT.name:
                    config.iconName = 'generic-luggage';
                    break;
                case TYPE_EMISSION.name:
                    config.iconName = 'generic-leaf';
                    config.iconColorClass = 'color--accent';
                    break;
                case TYPE_CONSUMPTION.name:
                    config.iconName = 'generic-fuel';
                    config.iconColorClass = 'color--accent';
                    break;
            }

            return config;
        },
        rating() {
            if (!this.readOnly || isNaN(this.value) || !(this.value > -1)) {
                return [0, false];
            }

            let percentage = 0;

            if (this.percentage) {
                percentage = this.percentage;
            } else {
                percentage = (100 * this.value) / this.config.maxValue;
            }

            // when type is `consumption` or `emission` the logic it's inverse: less it's better
            if (this.lessIsBetter) {
                percentage = 100 - percentage;

                // when percentage it's equal or less then `0`
                // we set a fixed percentage of `10` in order to show
                // the first half icon colored
                if (percentage < 1) {
                    percentage = 10;
                }
            }

            const value = (this.config.ratingMaxValue * percentage) / 100;
            const floorValue = Math.floor(value);

            return [
                floorValue < value ? floorValue + 1 : floorValue,
                floorValue < value ? Math.round((value - floorValue) * 100) : false,
            ];
        },
        ratingIsZero() {
            return this.rating[0] === 0 && !this.rating[1];
        },
        iconColorClass() {
            if (this.lessIsBetter && this.readOnly && this.rating[0] < 3) {
                return 'color--badge-orange';
            }

            return this.config.iconColorClass;
        },
        lessIsBetter() {
            return this.type === TYPE_CONSUMPTION.name || this.type === TYPE_EMISSION.name;
        },
    },
    methods: {
        async onRatingItemClick(index, event) {
            const ratingItemElm = event && event.target && event.target.parentNode;
            const checkedClass = 'dk-rating__item--checked';
            const { hasClass, removeClass, addClass } = await import('@v4/common/utils/dom');
            if (
                this.readOnly ||
                !ratingItemElm ||
                hasClass(ratingItemElm, checkedClass) ||
                !hasClass(ratingItemElm, 'dk-rating__item')
            ) {
                return;
            }

            const checkedItem = this.$el.querySelector(`.${checkedClass}`);
            if (checkedItem) {
                removeClass(checkedItem, checkedClass);
                checkedItem.setAttribute('aria-checked', false);
                checkedItem.setAttribute('tabindex', -1);
            }

            // update current item
            addClass(ratingItemElm, checkedClass);
            ratingItemElm.setAttribute('aria-checked', true);
            ratingItemElm.setAttribute('tabindex', 0);

            // update selected item
            this.selectedItem = { index: index + 1, el: ratingItemElm };
        },
        checked(i) {
            return i + 1 === this.rating[0];
        },
        isHalf(i) {
            return this.checked(i) && this.rating[1];
        },
    },
};
</script>
